import { useCallback, useEffect, useRef } from 'react';
import type { Howl, HowlOptions } from 'howler';
import useSound from 'use-sound';

const useBackgroundSound = (
	url: string,
	enabled: boolean,
	volume: number
): void => {
	const howlRef = useRef<Howl>();

	useEffect(() => {
		import('howler').then((mod) => {
			const options: HowlOptions = {
				src: [url],
				/*
				 * The html5 flag is necessary because otherwise an HTTP request is used to load the file.
				 * This would prevent the sounds from being loaded when the app is opened from the local disk.
				 */
				html5: true,
				volume,
				autoplay: enabled,
				loop: true,
				onplayerror: () => {
					howlRef.current?.once('unlock', () => {
						if (enabled) {
							howlRef.current?.play();
						}
					});
				}
			};

			howlRef.current = new mod.Howl(options);
		});

		return () => {
			howlRef.current?.stop();
			// set ref to undefined to avoid other effects
			howlRef.current = undefined;
		};
	}, []);

	useEffect(() => {
		const sound = howlRef.current;
		if (sound) {
			if (enabled) {
				sound.play();
			} else {
				sound.stop();
			}
		}
	}, [howlRef, enabled]);

	useEffect(() => {
		const sound = howlRef.current;
		if (sound) {
			sound.volume(volume);
		}
	}, [howlRef, volume]);
};

/**
 * Plays the effect sound
 *
 * @param duration
 * The duration over which the sound should be played. If not specified, the duration of the audio file is used.
 */
type PlayFunction = (duration?: number) => void;

const useEffectSound = (
	url: string,
	enabled: boolean,
	volume: number
): PlayFunction => {
	const options = {
		/*
		 * The html5 flag is necessary because otherwise an HTTP request is used to load the file.
		 * This would prevent the sounds from being loaded when the app is opened from the local disk.
		 */
		html5: true,
		soundEnabled: enabled,
		volume
	};
	const [play] = useSound(url, options);

	return useCallback(
		(duration?: number): void => {
			if (enabled) {
				if (duration == null) {
					play();
				} else if (duration > 0) {
					// TODO: Decide if it makes sense to adjust the playback rate of the sound. Some sounds sound strange as a result.
					/*
				if (info.duration) {
					const playbackRate = duration / info.duration;
					play({ playbackRate });
					play();
				} else {
					play();
				}
					*/
					play();
				}
			}
		},
		[enabled, play]
	);
};

export { useBackgroundSound, useEffectSound };
